@let analyses = analysisMap();
@let entryAnalyses = entryAnalysisMap();
@let collapsed = collapsedIds();
@let expandedBundles = expandedBundleIds();
@let statusHistoryIds = statusHistoryShiftIds();
@let hrefs = serviceHrefs();

<ng-template #editedLine let-adjustment>
  @if (adjustment) {
    <div>{{ 'TIMECARD_EDITED_BY' | translate:{name: adjustment.updatedBy, date: (adjustment | bringozFullDate:'updatedAt'), time: (adjustment | bringozTime:'updatedAt')} }}</div>
  }
</ng-template>

<div id="stops-table-body" class="stops-table-body flex layout-column">

  <!-- Empty State -->
  @if (!shifts().length && !leaves().length) {
    <div class="flex-100 layout-column layout-align-center-center fade-element-in-only">
      <bz-empty-state image="assets/images/empty_state/empty_state_timeline.png" [title]="'No Shifts Found' | translate"></bz-empty-state>
    </div>
  }

  @for (shift of shifts(); track shift.id) {
    @let shiftAnalysis = analyses[shift.id];
    @let shiftRisks = shiftAnalysis?.reportedData?.risks;
    @let isCollapsed = collapsed.has(shift.id);
    @let isReviewable = shift.status === TimeCardStatusType.Closed || shift.status === TimeCardStatusType.Acknowledged;

    <ng-template #shiftRisksTooltip>
      @for (risk of shiftRisks; track $index) {
        <div>{{ 'TIMECARD_DATA_RISK.' + risk | translate }}</div>
      }
    </ng-template>

    <div class="shift-container">

      <!-- Shift Header Row -->
      <div class="stop-row shift-header-row layout-row layout-align-start-center"
           appBringozIdGenerator="timecard-shift-header-row"
           [class.shift-selected]="selectedShiftId() === shift.id"
           (click)="onShiftHeaderClick(shift, $event)">
        <div class="cell stop-cell">
          <bz-icon class="margin-right-5" [icon]="isCollapsed ? icons.collapsed : icons.expanded"></bz-icon>
          @if (shift.appUsed === false) {
            <bz-icon class="margin-right-5 no-app" [icon]="icons.noApp"
                     [label]="'TIMECARD_NO_APP_USED' | translate"
                     [ngbTooltip]="'TIMECARD_NO_APP_USED' | translate" container="body"></bz-icon>
          }
          <span class="font-weight-600">{{ shift.shiftName }}</span>
          @if (shiftAnalysis?.hasInfeasibleRisk) {
            <bz-icon class="risk-icon-infeasible" [icon]="icons.risk"
                     [ngbTooltip]="shiftRisksTooltip" tooltipClass="entry-risk-tooltip" container="body"></bz-icon>
          } @else if (shiftAnalysis?.hasWarningRisk) {
            <bz-icon class="risk-icon-warning" [icon]="icons.risk"
                     [ngbTooltip]="shiftRisksTooltip" tooltipClass="entry-risk-tooltip" container="body"></bz-icon>
          }
        </div>
        <div class="cell miles-cell"><span class="column-header">{{ siteDistanceUnit() }}</span></div>
        <div class="cell drive-time-cell"><span class="column-header">{{ 'Drive Time' | translate }}</span></div>
        <div class="cell arrival-cell"><span class="column-header">{{ 'Arrival' | translate }}</span></div>
        <div class="cell arrival-gps-cell"><span class="column-header">{{ 'Arrival GPS' | translate }}</span></div>
        <div class="cell departure-cell"><span class="column-header">{{ 'Departure' | translate }}</span></div>
        <div class="cell dep-gps-cell"><span class="column-header">{{ 'Dep. GPS' | translate }}</span></div>
        <div class="cell service-time-cell"><span class="column-header">{{ 'Service Time' | translate }}</span></div>
        <div class="cell planned-time-cell"><span class="column-header">{{ 'Planned Time' | translate }}</span></div>
        <div class="cell flag-cell"><span class="column-header">{{ 'Flag' | translate }}</span></div>
        <div class="cell status-cell">
          @if (shift.status === TimeCardStatusType.Open) {
            <bz-button variant="tertiary" tone="primary" size="fit"
                       appBringozIdGenerator="timecard-shift-refresh-button"
                       [ariaLabel]="'Refresh' | translate"
                       [disabled]="isRefreshingData() || isStatusChanging()"
                       (clicked)="onRefresh($event, shift)">
              <bz-icon [icon]="icons.refresh"></bz-icon>
            </bz-button>
          }
          <div class="status-badges-row layout-row layout-align-center-center">
            @if (shift.hasStatusOptions && !isStatusChanging()) {
              <bz-menu appBringozIdGenerator="timecard-shift-status-menu"
                       [items]="statusMenuItems()[shift.id]"
                       [ariaLabel]="'Change Status' | translate"
                       (click)="$event.stopPropagation()"
                       (selected)="onStatusSelected(shift, $event)">
                <span class="layout-row layout-align-start-center">
                  <bz-tag [tone]="shift.status | timecardStatusTone">{{ 'TIMECARD_SHIFT_STATUS.' + shift.status | translate }}</bz-tag>
                  <bz-icon size="sm" [icon]="icons.expanded"></bz-icon>
                </span>
              </bz-menu>
            } @else {
              <bz-tag [tone]="shift.status | timecardStatusTone">{{ 'TIMECARD_SHIFT_STATUS.' + shift.status | translate }}</bz-tag>
            }
            @if (statusHistoryIds.has(shift.id)) {
              <bz-button variant="tertiary" tone="primary" size="fit" class="status-history-icon"
                         appBringozIdGenerator="timecard-shift-status-history-button"
                         [ariaLabel]="'History' | translate"
                         (clicked)="onOpenStatusHistory($event, shift)">
                <bz-icon [icon]="icons.history"></bz-icon>
              </bz-button>
            }
            @if (isReviewable && shift.hasInfeasibility) {
              <ng-template #infeasibleTooltip>
                <div><strong>{{ 'Timecard blocked. Please review and fix data to proceed.' | translate }}</strong></div>
                @for (risk of shiftRisks; track $index) {
                  <div>{{ 'TIMECARD_DATA_RISK.' + risk | translate }}</div>
                }
              </ng-template>
              <bz-tag tone="danger" [ngbTooltip]="infeasibleTooltip" tooltipClass="entry-risk-tooltip" container="body">{{ 'Infeasible' | translate }}</bz-tag>
            }
            @if (isReviewable && shiftAnalysis?.hasWarningRisk && !shift.hasInfeasibility) {
              <bz-tag tone="warning" [ngbTooltip]="shiftRisksTooltip" tooltipClass="entry-risk-tooltip" container="body">{{ 'Warning' | translate }}</bz-tag>
            }
          </div>
        </div>
      </div>

      <!-- Stop Rows -->
      @if (!isCollapsed) {
        @for (stop of shift.stops; track stop.id) {
          @let entryAnalysis = entryAnalyses[shift.id]?.[stop.id];
          @let isBundleExpanded = expandedBundles.has(stop.id);
          @let canEditDistance = shift.isEditable && !stop.isAgentLocationEntry && !stop.isBreakEntry;
          @let canEditArrival = shift.isEditable && !stop.isEndLocationEntry;
          @let canEditDeparture = shift.isEditable && !stop.isStartLocationEntry;
          @let canEditServiceTime = shift.isEditable && !stop.isAgentLocationEntry;

          <ng-template #entryRisksTooltip>
            @for (risk of entryAnalysis?.risks; track $index) {
              <div>{{ 'TIMECARD_ENTRY_RISK.' + risk | translate }}</div>
            }
          </ng-template>

          <div class="stop-row layout-row layout-align-start-center"
               appBringozIdGenerator="timecard-stop-row"
               [class.selected]="selectedShiftId() === shift.id && selectedTaskId() === stop.id"
               [class.bundle-expanded]="isBundleExpanded"
               [class.risk-row-infeasible]="entryAnalysis?.hasInfeasibleRisk"
               [class.risk-row-warning]="entryAnalysis?.hasWarningRisk"
               (click)="stopSelect.emit({stop: stop, shiftId: shift.id})">

            <!-- Stop / address -->
            <div *ngxPermissionsOnly="'timekeeping.timeCard.update'"
                 class="cell stop-cell"
                 [class.bundle-stop-cell]="stop.isBundle"
                 [class.adjusted-cell]="stop.isLocationAdjusted"
                 [class.adjusted-console-cell]="stop.locationAdjustmentSource === UpdateSource.Console"
                 [class.adjusted-agent-cell]="stop.locationAdjustmentSource === UpdateSource.Agent"
                 [ngbTooltip]="stop.locationAdjustment ? locationTooltip : null" container="body">
              <ng-template #locationTooltip>
                <ng-container *ngTemplateOutlet="editedLine; context: {$implicit: stop.locationAdjustment}"></ng-container>
              </ng-template>
              @if (stop.isBundle || stop.stopNumber != null) {
                <div class="stop-number-col layout-column layout-align-start-center">
                  @if (stop.isBundle) {
                    <bz-button class="bundle-expand-button" variant="tertiary" tone="primary" size="fit"
                               appBringozIdGenerator="timecard-stop-bundle-toggle-button"
                               [ariaLabel]="(isBundleExpanded ? 'Collapse' : 'Expand') | translate"
                               (clicked)="onToggleBundle(stop, $event)">
                      <bz-icon [icon]="isBundleExpanded ? icons.expanded : icons.collapsed"></bz-icon>
                    </bz-button>
                  }
                  @if (stop.stopNumber != null) {
                    <span class="stop-number">{{ stop.stopNumber }}</span>
                  }
                </div>
              }
              <div class="stop-info layout-column">
                @if (stop.isBundle) {
                  <div class="bundle-badge-row layout-row">
                    <bz-tag tone="info">
                      <span class="layout-row layout-align-start-center gap-5">
                        <bz-icon size="sm" [icon]="icons.bundle"></bz-icon>
                        <span>{{ (stop.serviceIds.length === 1 ? 'TIMECARD_BUNDLED_STOP' : 'TIMECARD_BUNDLED_STOPS') | translate:{count: stop.serviceIds.length} }}</span>
                      </span>
                    </bz-tag>
                  </div>
                }
                <div class="layout-row layout-align-start-center">
                  @if (!stop.isBundle && stop.serviceIds?.length) {
                    <a class="font-weight-500 service-link"
                       target="_blank"
                       [href]="hrefs[stop.serviceIds[0]]"
                       (click)="$event.stopPropagation()">{{ stop.name }}@if (stop.externalId) {<span class="external-id"> {{ stop.externalId }}</span>}</a>
                  } @else {
                    <span class="font-weight-500">{{ stop.name }}@if (stop.externalId) {<span class="external-id"> {{ stop.externalId }}</span>}</span>
                  }
                </div>
                @if (stop.locationType) {
                  <span class="location-type br-caption color-gray-400">{{ 'TIMECARD_LOCATION_TYPE.' + stop.locationType | translate }}</span>
                }
                @if (stop.isLocationAdjusted) {
                  <span class="adjusted-value stop-address">
                    {{ stop.address || '---' }}
                    @if (stop.initialLocation?.formattedAddress) {
                      <span class="initial-value">({{ stop.initialLocation.formattedAddress }})</span>
                    }
                  </span>
                } @else {
                  <span class="stop-address">{{ stop.address || '---' }}</span>
                }
              </div>
              @if (stop.isAgentLocationEntry) {
                <bz-icon class="history-log-icon" [icon]="icons.history" [label]="'History' | translate" (click)="onOpenHistory($event, stop, shift, 'location')"></bz-icon>
              }
            </div>

            <!-- Distance -->
            <div *ngxPermissionsOnly="'timekeeping.timeCard.update'"
                 class="cell miles-cell"
                 appBringozIdGenerator="timecard-stop-distance-cell"
                 [class.editable-cell]="canEditDistance"
                 [class.adjusted-cell]="stop.isDistanceAdjusted"
                 [class.adjusted-console-cell]="stop.distanceAdjustmentSource === UpdateSource.Console"
                 [class.adjusted-agent-cell]="stop.distanceAdjustmentSource === UpdateSource.Agent"
                 [ngbTooltip]="stop.hasPlannedData || stop.distanceAdjustment ? distanceTooltip : null" container="body"
                 (click)="onEditEntry($event, stop, shift, 'distance', canEditDistance)">
              <ng-template #distanceTooltip>
                @if (stop.hasPlannedData) {
                  <div>{{ 'Planned Distance' | translate }}: {{ stop.plannedDistance != null ? (stop.plannedDistance | bringozDistance:'---':true:2) : '---' }}</div>
                }
                <ng-container *ngTemplateOutlet="editedLine; context: {$implicit: stop.distanceAdjustment}"></ng-container>
              </ng-template>
              @if (stop.isDistanceAdjusted) {
                <span class="adjusted-value">
                  {{ stop.miles | bringozDistance:'---':true:2 }}
                  <span [class.initial-value]="stop.initialDistance !== undefined">({{ stop.initialDistance != null ? (stop.initialDistance | bringozDistance:'---':true:2) : '---' }})</span>
                </span>
              } @else {
                <span>{{ stop.miles != null ? (stop.miles | bringozDistance:'---':true:2) : '---' }}</span>
              }
              @if (!stop.isAgentLocationEntry && !stop.isBreakEntry) {
                <bz-icon class="history-log-icon" [icon]="icons.history" [label]="'History' | translate" (click)="onOpenHistory($event, stop, shift, 'drivingDistanceFromPrevStop')"></bz-icon>
              }
              @if (canEditDistance) {
                <bz-icon class="edit-icon" size="sm" [icon]="icons.edit"></bz-icon>
              }
            </div>

            <!-- Drive time -->
            <div class="cell drive-time-cell"><span>{{ stop.driveTime ? (stop.driveTime | bringozDurationExact) : '---' }}</span></div>

            <!-- Arrival -->
            <div *ngxPermissionsOnly="'timekeeping.timeCard.update'"
                 class="cell arrival-cell"
                 appBringozIdGenerator="timecard-stop-arrival-cell"
                 [class.editable-cell]="canEditArrival"
                 [class.adjusted-cell]="stop.isArrivalAdjusted"
                 [class.adjusted-console-cell]="stop.arrivalAdjustmentSource === UpdateSource.Console"
                 [class.adjusted-agent-cell]="stop.arrivalAdjustmentSource === UpdateSource.Agent"
                 [ngbTooltip]="stop.hasPlannedData || stop.arrivalAdjustment ? arrivalTooltip : null" container="body"
                 (click)="onEditEntry($event, stop, shift, 'arrival', canEditArrival)">
              <ng-template #arrivalTooltip>
                @if (stop.hasPlannedData) {
                  <div>{{ 'Planned Arrival' | translate }}: {{ stop | bringozTime:'plannedArrival':'---' }}</div>
                }
                <ng-container *ngTemplateOutlet="editedLine; context: {$implicit: stop.arrivalAdjustment}"></ng-container>
              </ng-template>
              @if (stop.isEndLocationEntry) {
                <span>---</span>
              } @else {
                <span class="layout-row layout-align-start-center" [class.adjusted-value]="stop.isArrivalAdjusted">
                  @if (stop.isArrivalAdjusted) {
                    <span>{{ stop | bringozTime:'arrival':'---' }} <span [class.initial-value]="stop.initialArrival">({{ stop | bringozTime:'initialArrival':'---' }})</span></span>
                  } @else {
                    <span>{{ stop.arrival ? (stop | bringozTime:'arrival':'---') : '---' }}</span>
                  }
                  @if (stop.arrival && stop.zoneId) {
                    <app-bringoz-time-window-tooltip [notBefore]="stop.arrival" [notAfter]="stop.arrival"
                                                     [timeZoneId]="stop.zoneId" [configZoneId]="configTimeZone()"
                                                     [isTooltip]="true">
                    </app-bringoz-time-window-tooltip>
                  }
                </span>
                <bz-icon class="history-log-icon" [icon]="icons.history" [label]="'History' | translate" (click)="onOpenHistory($event, stop, shift, 'startTime')"></bz-icon>
              }
              @if (canEditArrival) {
                <bz-icon class="edit-icon" size="sm" [icon]="icons.edit"></bz-icon>
              }
            </div>

            <!-- Arrival GPS -->
            <div class="cell arrival-gps-cell layout-row layout-align-start-center">
              @if (stop.isEndLocationEntry) {
                <span>---</span>
              } @else {
                <span [class.gps-time-mismatch]="stop.arrivalGps && entryAnalysis?.hasArrivalTimeMismatch"
                      [class.gps-time-match]="stop.arrivalGps && shiftAnalysis && !entryAnalysis?.hasArrivalTimeMismatch">{{ stop.arrivalGps ? (stop | bringozTime:'arrivalGps':'---') : '---' }}</span>
                @if (stop.arrivalGps && stop.zoneId) {
                  <app-bringoz-time-window-tooltip [notBefore]="stop.arrivalGps" [notAfter]="stop.arrivalGps"
                                                   [timeZoneId]="stop.zoneId" [configZoneId]="configTimeZone()"
                                                   [isTooltip]="true">
                  </app-bringoz-time-window-tooltip>
                }
              }
            </div>

            <!-- Departure -->
            <div *ngxPermissionsOnly="'timekeeping.timeCard.update'"
                 class="cell departure-cell"
                 appBringozIdGenerator="timecard-stop-departure-cell"
                 [class.editable-cell]="canEditDeparture"
                 [class.adjusted-cell]="stop.isDepartureAdjusted"
                 [class.adjusted-console-cell]="stop.departureAdjustmentSource === UpdateSource.Console"
                 [class.adjusted-agent-cell]="stop.departureAdjustmentSource === UpdateSource.Agent"
                 [ngbTooltip]="stop.hasPlannedData || stop.departureAdjustment ? departureTooltip : null" container="body"
                 (click)="onEditEntry($event, stop, shift, 'departure', canEditDeparture)">
              <ng-template #departureTooltip>
                @if (stop.hasPlannedData) {
                  <div>{{ 'Planned Departure' | translate }}: {{ stop | bringozTime:'plannedDeparture':'---' }}</div>
                }
                <ng-container *ngTemplateOutlet="editedLine; context: {$implicit: stop.departureAdjustment}"></ng-container>
              </ng-template>
              @if (stop.isStartLocationEntry) {
                <span>---</span>
              } @else {
                <span class="layout-row layout-align-start-center" [class.adjusted-value]="stop.isDepartureAdjusted">
                  @if (stop.isDepartureAdjusted) {
                    <span>{{ stop | bringozTime:'departure':'---' }} <span [class.initial-value]="stop.initialDeparture">({{ stop | bringozTime:'initialDeparture':'---' }})</span></span>
                  } @else {
                    <span>{{ stop.departure ? (stop | bringozTime:'departure':'---') : '---' }}</span>
                  }
                  @if (stop.departure && stop.zoneId) {
                    <app-bringoz-time-window-tooltip [notBefore]="stop.departure" [notAfter]="stop.departure"
                                                     [timeZoneId]="stop.zoneId" [configZoneId]="configTimeZone()"
                                                     [isTooltip]="true">
                    </app-bringoz-time-window-tooltip>
                  }
                </span>
                <bz-icon class="history-log-icon" [icon]="icons.history" [label]="'History' | translate" (click)="onOpenHistory($event, stop, shift, 'endTime')"></bz-icon>
              }
              @if (canEditDeparture) {
                <bz-icon class="edit-icon" size="sm" [icon]="icons.edit"></bz-icon>
              }
            </div>

            <!-- Departure GPS -->
            <div class="cell dep-gps-cell layout-row layout-align-start-center">
              @if (stop.isStartLocationEntry) {
                <span>---</span>
              } @else {
                <span [class.gps-time-mismatch]="stop.depGps && entryAnalysis?.hasDepartureTimeMismatch"
                      [class.gps-time-match]="stop.depGps && shiftAnalysis && !entryAnalysis?.hasDepartureTimeMismatch">{{ stop.depGps ? (stop | bringozTime:'depGps':'---') : '---' }}</span>
                @if (stop.depGps && stop.zoneId) {
                  <app-bringoz-time-window-tooltip [notBefore]="stop.depGps" [notAfter]="stop.depGps"
                                                   [timeZoneId]="stop.zoneId" [configZoneId]="configTimeZone()"
                                                   [isTooltip]="true">
                  </app-bringoz-time-window-tooltip>
                }
              }
            </div>

            <!-- Service time -->
            <div *ngxPermissionsOnly="'timekeeping.timeCard.update'"
                 class="cell service-time-cell"
                 appBringozIdGenerator="timecard-stop-service-time-cell"
                 [class.editable-cell]="canEditServiceTime"
                 [class.adjusted-cell]="stop.isServiceTimeAdjusted"
                 [class.adjusted-console-cell]="stop.serviceTimeAdjustmentSource === UpdateSource.Console"
                 [class.adjusted-agent-cell]="stop.serviceTimeAdjustmentSource === UpdateSource.Agent"
                 [ngbTooltip]="stop.hasPlannedData || stop.serviceTimeAdjustment ? serviceTimeTooltip : null" container="body"
                 (click)="onEditEntry($event, stop, shift, 'serviceTime', canEditServiceTime)">
              <ng-template #serviceTimeTooltip>
                @if (stop.hasPlannedData) {
                  <div>{{ 'Planned Service Time' | translate }}: {{ stop.plannedServiceTime ? (stop.plannedServiceTime | bringozDurationExact) : '---' }}</div>
                }
                <ng-container *ngTemplateOutlet="editedLine; context: {$implicit: stop.serviceTimeAdjustment}"></ng-container>
              </ng-template>
              @if (stop.isAgentLocationEntry) {
                <span>---</span>
              } @else {
                @if (stop.isServiceTimeAdjusted) {
                  <span class="adjusted-value">
                    {{ stop.serviceTime | bringozDurationExact }}
                    <span [class.initial-value]="stop.initialServiceTime">({{ stop.initialServiceTime | bringozDurationExact }})</span>
                  </span>
                } @else {
                  <span>{{ stop.serviceTime ? (stop.serviceTime | bringozDurationExact) : '---' }}</span>
                }
                <bz-icon class="history-log-icon" [icon]="icons.history" [label]="'History' | translate" (click)="onOpenHistory($event, stop, shift, 'serviceTime')"></bz-icon>
              }
              @if (canEditServiceTime) {
                <bz-icon class="edit-icon" size="sm" [icon]="icons.edit"></bz-icon>
              }
            </div>

            <!-- Planned time -->
            <div class="cell planned-time-cell">
              <span>{{ stop.isAgentLocationEntry ? '---' : (stop.plannedServiceTime ? (stop.plannedServiceTime | bringozDurationExact) : '---') }}</span>
            </div>

            <!-- Flag -->
            <div class="cell flag-cell">
              @if (entryAnalysis?.risks?.length) {
                <bz-icon class="entry-risk-icon risk-icon-infeasible" [icon]="icons.risk"
                         [ngbTooltip]="entryRisksTooltip" tooltipClass="entry-risk-tooltip"
                         placement="left" container="body"></bz-icon>
              }
            </div>

            <!-- Entry status -->
            <div class="cell status-cell">
              @if (entryAnalysis?.hasInfeasibleRisk) {
                <bz-tag tone="danger" [ngbTooltip]="entryRisksTooltip" tooltipClass="entry-risk-tooltip" placement="left" container="body">{{ 'Infeasible' | translate }}</bz-tag>
              }
              @if (entryAnalysis?.hasWarningRisk) {
                <bz-tag tone="warning" [ngbTooltip]="entryRisksTooltip" tooltipClass="entry-risk-tooltip" placement="left" container="body">{{ 'Warning' | translate }}</bz-tag>
              }
            </div>

            @if (!stop.isAgentLocationEntry && isReviewable) {
              <bz-button class="delete-btn" variant="tertiary" tone="danger" size="fit"
                         appBringozIdGenerator="timecard-stop-archive-button"
                         [ariaLabel]="'Archive' | translate"
                         (clicked)="onArchive($event, stop, shift)">
                <bz-icon [icon]="icons.archive"></bz-icon>
              </bz-button>
            }
          </div>

          <!-- Bundle Tasks (expanded bundle) -->
          @if (stop.isBundle && isBundleExpanded) {
            <div class="bundle-tasks-row">
              @for (serviceId of stop.serviceIds; track $index) {
                <div class="bundle-task-item layout-row layout-align-start-center">
                  <span class="bundle-task-label">{{ 'TIMECARD_BUNDLE_TASK_NUMBER' | translate:{number: $index + 1} }}</span>
                  <a class="bundle-service-link" target="_blank" [href]="hrefs[serviceId]">{{ serviceId }}</a>
                </div>
              }
            </div>
          }
        }

        <!-- Totals Row -->
        @if (shift.stops.length) {
          <div class="stop-row totals-row layout-row layout-align-start-center">
            <div class="cell stop-cell">
              <span class="font-weight-600">{{ 'Totals' | translate }}</span>
              <span class="font-weight-500 margin-left-5">({{ shift.totals.totalStops }} {{ 'Stops' | translate }})</span>
            </div>
            <div class="cell miles-cell">
              <span class="font-weight-600">{{ shift.totals.totalDistance | bringozDistance:'---':true:2 }}</span>
            </div>
            <div class="cell drive-time-cell">
              <span class="font-weight-600">{{ shift.totals.totalDriveTime | bringozDurationExact }}</span>
            </div>
            <div class="cell arrival-cell"></div>
            <div class="cell arrival-gps-cell"></div>
            <div class="cell departure-cell"></div>
            <div class="cell dep-gps-cell"></div>
            <div class="cell service-time-cell">
              <span class="font-weight-600">{{ shift.totals.totalServiceTime | bringozDurationExact }}</span>
            </div>
            <div class="cell planned-time-cell">
              <span class="font-weight-600">{{ shift.totals.totalPlannedTime | bringozDurationExact }}</span>
            </div>
            <div class="cell flag-cell"></div>
            <div class="cell status-cell"></div>
          </div>
        }
      }

    </div>
  }

  <!-- Leave Sections - each leave timecard gets its own header -->
  @for (leave of leaves(); track leave.id) {
    @let isLeaveCollapsed = collapsed.has(leave.id);
    <div class="shift-container leave-container">

      <div class="stop-row shift-header-row leave-header-row layout-row layout-align-start-center"
           (click)="onLeaveHeaderClick(leave, $event)">
        <div class="cell leave-name-cell">
          <bz-icon class="margin-right-5" [icon]="isLeaveCollapsed ? icons.collapsed : icons.expanded"></bz-icon>
          <span class="font-weight-600">{{ 'Leave' | translate }}</span>
        </div>
        <div class="cell leave-paid-cell"><span class="column-header">{{ 'Paid' | translate }}</span></div>
        <div class="cell leave-arrival-cell"><span class="column-header">{{ 'Arrival' | translate }}</span></div>
        <div class="cell flex"></div>
        <div class="cell status-cell"></div>
      </div>

      @if (!isLeaveCollapsed) {
        <div class="stop-row leave-row layout-row layout-align-start-center">
          <div class="cell leave-name-cell">
            <span class="font-weight-500">{{ leave.absenceReasonName }}</span>
          </div>
          <div class="cell leave-paid-cell">
            @if (leave.payable) {
              <bz-icon class="leave-check-icon" [icon]="icons.check" [label]="'Paid' | translate"></bz-icon>
            }
          </div>
          <div class="cell leave-arrival-cell">
            <span>{{ {from: leave.clockInTime, to: leave.clockOutTime} | bringozDateRange:true }}</span>
          </div>
          <div class="cell flex"></div>
          <div class="cell status-cell">
            <bz-tag [tone]="'Approved' | timecardStatusTone">{{ 'Approved' | translate }}</bz-tag>
          </div>
        </div>
      }

    </div>
  }

</div>
